import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react';
import { routes } from '../../../env/routes';
import {
  formatAddress,
  getPriceLabel,
} from '@portal/env/utils/eventDetailsHelpers';
import { DateTimeIcon, LocationIcon } from '@common/bw-ui/icons';
import { Button } from '@common/bw-ui/ui/button';
import { DateTime } from 'luxon';
import { Event } from '../../../types/nexudus/events';
import { EventCardCTA } from './EventCardCTA';
import { documentToPlainTextString } from '@portal/env/utils/textHelpers';
import { Card } from '@common/bw-ui/ui/card';
import { useApp } from '../hooks/useApp';
import { useGymWaiverStatus } from '@portal/ui/hooks/useGymWaiverStatus';
import { WAIVER_STATE } from '@portal/env/stores/authTokens/IProfileTokenPayload';
import { checkHideTickets } from '@portal/env/utils/parqHelpers';

const EventCard: React.FC<{ event: Event; isGymClass?: boolean }> = observer(
  ({ event, isGymClass = false }) => {
    const { resourceImageBaseUrl } = useApp();
    const router = useRouter();
    const { gymWaiverStatus } = useGymWaiverStatus();
    const hasValidGymWaiver = gymWaiverStatus !== WAIVER_STATE.NOT_FOUND;
    const hideTickets = checkHideTickets(event, null, hasValidGymWaiver);

    const start = DateTime.fromISO(event.StartDate);
    const end = DateTime.fromISO(event.EndDate || event.StartDate);
    const isSameDay = start.hasSame(end, 'day');

    const datetimeLabel = isSameDay
      ? `${start.toFormat('HH:mm')} - ${end.toFormat('HH:mm')}`
      : `${start.toFormat('d LLL HH:mm')} - ${end.toFormat('d LLL HH:mm')}`;

    const shortDateLabel = start.toFormat('d LLL');

    const formattedTicketPrice = getPriceLabel(
      event.ChepeastPrice,
      event.MostExpensivePrice,
    );

    const formattedVenueAddress = event
      ? formatAddress(event.VenueAddress)
      : '';

    const showCTA = !hideTickets && !!EventCardCTA({ event });

    if (!event) return null;

    const shortDescriptionPlain = documentToPlainTextString(
      event.ShortDescription,
    );

    return (
      <Card className="tw:flex tw:flex-col tw:relative tw:w-full tw:h-full tw:border tw:border-gray-300 tw:overflow-hidden">
        <div
          className="tw:cursor-pointer"
          onClick={() =>
            router.push(
              isGymClass
                ? routes.gym_class_by_id(event)
                : routes.event_by_id(event),
            )
          }
        >
          <div className="tw:relative tw:w-full tw:h-[200px] tw:rounded-t-lg tw:overflow-hidden">
            <img
              src={`${resourceImageBaseUrl}/events/getlargeimage?id=${event.Id}&w=800&h=400&mode=Crop&cache=${event.UpdatedOn}`}
              alt={event.Name}
              className="tw:w-full tw:h-full tw:object-cover"
            />
            <span className="tw:absolute tw:bottom-2 tw:left-4 tw:bg-white tw:text-black tw:px-2 tw:text-xs tw:font-normal tw:py-1 tw:rounded">
              {shortDateLabel}
            </span>
            {event.EventCategories.length > 0 && (
              <span className="tw:absolute tw:bottom-2 tw:right-4 tw:bg-white tw:text-black tw:px-2 tw:text-xs tw:font-normal tw:py-1 tw:rounded">
                {event.EventCategories[0]?.Title}
              </span>
            )}
          </div>

          <div className="tw:flex tw:flex-col tw:flex-1 tw:px-4 tw:mt-2">
            <div className="tw:flex tw:justify-between tw:items-start">
              <h5 className="tw:text-lg tw:font-medium tw:line-clamp-2">
                {event.Name}
              </h5>
              <span
                className={`tw:px-3 tw:py-1 tw:ml-1 tw:text-sm tw:font-normal tw:rounded-lg tw:whitespace-nowrap tw:flex-shrink-0 ${formattedTicketPrice === 'Free' ? 'bg-green tw:text-white' : 'tw:bg-gray-200 tw:text-gray-800'}`}
              >
                {formattedTicketPrice}
              </span>
            </div>
            <p className="tw:prose tw:prose-slate tw:max-w-none tw:text-slate-900 tw:font-light tw:text-sm tw:mb-4 tw:line-clamp-2">
              {shortDescriptionPlain}
            </p>

            <div className="tw:flex tw:flex-col tw:gap-2 tw:mb-4 tw:mt-auto">
              {formattedVenueAddress && (
                <div className="tw:flex tw:items-center tw:gap-2">
                  <LocationIcon className="tw:w-6 tw:h-6 tw:text-red-400" />
                  <p className="tw:text-gray-500 tw:font-light tw:text-sm tw:m-0">
                    {formattedVenueAddress}
                  </p>
                </div>
              )}
              <div className="tw:flex tw:items-center tw:gap-2">
                <DateTimeIcon className="tw:w-6 tw:h-6 tw:text-red-400" />
                <div className="tw:flex tw:flex-col">
                  <p className="tw:text-sm tw:font-light tw:text-gray-500 tw:m-0">
                    {datetimeLabel}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`tw:w-full tw:p-2 tw:mt-auto tw:flex tw:flex-col tw:sm:flex-row tw:justify-between tw:bg-gray-200 tw:gap-2`}
        >
          {!hideTickets && <EventCardCTA event={event} />}
          <Button
            asChild
            variant="gray"
            size="lg"
            className={showCTA ? 'tw:w-full tw:sm:w-1/4' : 'tw:w-full'}
          >
            <Link
              href={
                isGymClass
                  ? routes.gym_class_by_id(event)
                  : routes.event_by_id(event)
              }
            >
              Details
            </Link>
          </Button>
        </div>
      </Card>
    );
  },
);

export default EventCard;
